// JobProgressPanel（V1.0 · 工业软件风重构）
// ---------------------------------------------------------------------
// 设计目标：让用户一眼看清「识别哪张图 / 当前阶段 / 用时 / 剩余 / 异常」。
// 数据完全来自后端 RecognitionJob，不伪造任何统计。
// 时间格式：支持 秒(<60，含一位小数) / 分秒 / 时分 / 日时 三档。
// 超时分级：OCR 阶段 ≥ 120s 显示提示；总 Job ≥ 300s 进入超时态并提示。
// ---------------------------------------------------------------------
import { useEffect, useState } from 'react';
import { getJob, retryJob, cancelJob } from '../api/client';
import type { RecognitionJob, JobStatus } from '../types';

// ---------------------------------------------------------------- 时间格式
// 设计：避免前版 `${m}m${ss}s` 把秒数当分钟计算（例如 28802s 被显示为 480m02s）。
// 新规则：<60s 显示 1 位小数；<1h 显示 "Xm YYs"；<1d 显示 "Xh YYm"；≥1d 显示 "Xd YYh"。
export function fmtDuration(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) sec = 0;
  if (sec < 60) return `${sec.toFixed(1)}s`;
  if (sec < 3600) {
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${m}m ${String(s).padStart(2, '0')}s`;
  }
  if (sec < 86400) {
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    return `${h}h ${String(m).padStart(2, '0')}m`;
  }
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  return `${d}d ${String(h).padStart(2, '0')}h`;
}

// 解析后端时间戳为毫秒。后端统一按 UTC 存储，但 SQLite 方言会把 tzinfo 丢掉，
// 序列化为 naive 串（如 "2026-09-06 05:59:18.164587"）。若前端按本地时区解析会偏
// 差 +8h（本机时区），导致「已耗时 8h」「已超过 4 分钟」等错误提示。
// 规则：串末尾带 Z 或 ±hh:mm 时区标记 → 原样 Date 解析；否则视为 UTC（补 Z）。
function parseUTCDate(s: string | null | undefined): number {
  if (!s) return 0;
  const str = String(s).trim();
  if (!str) return 0;
  if (/(Z|[+-]\d{2}:?\d{2})\s*$/i.test(str)) {
    const t = Date.parse(str);
    return Number.isNaN(t) ? 0 : t;
  }
  const norm = str.includes('T') ? str : str.replace(' ', 'T');
  const t = Date.parse(norm.endsWith('Z') ? norm : `${norm}Z`);
  return Number.isNaN(t) ? 0 : t;
}

// 后端超时预算（同步后端 T_TIFF/T_OCR/T_JOB，确保前端不会比后端更早宣布超时）
const T_OCR_SLOW_MS = 120 * 1000;
const T_JOB_SLOW_MS = 240 * 1000;

// ---------------------------------------------------------------- 步骤模型
// key：用于内部排序；match：哪些 JobStatus 算作该步骤「进行中」。
type StepKey = 'read' | 'convert' | 'ocr' | 'parse' | 'feature' | 'balloon';

interface Step {
  key: StepKey;
  label: string;                 // 显示名（工业风：动词 + 对象）
  hint: string;                  // 当前态时的解释文案
  match: JobStatus[];            // 视为「进行中」的状态集合
}

const STEPS: Step[] = [
  { key: 'read',    label: '文件读取',
    hint: '正在读取图纸文件并校验格式',
    match: ['PENDING', 'PROCESSING'] },
  { key: 'convert', label: '图像转换',
    hint: '正在将图纸转换为识别专用图像',
    match: ['DECODING', 'PREPROCESSING'] },
  { key: 'ocr',     label: 'OCR 文字识别',
    hint: '正在分析工程图中的文字、尺寸、公差和技术要求',
    match: ['OCR'] },
  { key: 'parse',   label: '尺寸 / GD&T 识别',
    hint: '正在提取尺寸、公差与几何公差符号',
    match: ['PARSING'] },
  { key: 'feature', label: 'Feature 候选生成',
    hint: '正在生成检验特征候选并分配编号',
    match: ['PARSING'] },
  { key: 'balloon', label: 'Balloon 生成',
    hint: '正在布置气球位置与引线',
    match: ['PARSING'] },
];

const TERMINAL: JobStatus[] = ['COMPLETED', 'FAILED', 'TIMEOUT', 'CANCELLED'];

// 简化版 stage→step 映射
const STAGE_TO_STEP: Record<string, StepKey | null> = {
  PENDING:       'read',
  PROCESSING:    'read',
  DECODING:      'convert',
  PREPROCESSING: 'convert',
  OCR:           'ocr',
  PARSING:       'parse',
  COMPLETED:     'balloon',  // 终态
  FAILED:        null,
  TIMEOUT:       null,
  CANCELLED:     null,
};

function activeStepKey(job: RecognitionJob): StepKey {
  if (job.status === 'COMPLETED') return 'balloon';
  const k = STAGE_TO_STEP[job.status];
  if (k) return k;
  // 失败类：用 stage 名回退。PARSING 阶段内 parse/feature/balloon 都可能进行，看进度粗判
  if (job.stage === 'PARSING') return 'parse';
  return 'read';
}

// ---------------------------------------------------------------- Props
interface Props {
  jobId: number;
  onDone?: (job: RecognitionJob) => void;
  onCancel?: () => void;
}

export default function JobProgressPanel({ jobId, onDone, onCancel }: Props) {
  const [job, setJob] = useState<RecognitionJob | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [, forceTick] = useState(0);
  const [showDetail, setShowDetail] = useState(false);

  // 轮询 + 每秒刷新当前已耗时
  useEffect(() => {
    let stop = false;
    let timer: number | undefined;
    async function tick() {
      try {
        const j = await getJob(jobId);
        if (stop) return;
        setJob(j);
        setErr(null);
        if (TERMINAL.includes(j.status)) {
          onDone?.(j);
          return;
        }
        timer = window.setTimeout(tick, 1200);
      } catch (e: any) {
        if (stop) return;
        setErr(e?.message ?? '查询失败');
        timer = window.setTimeout(tick, 2000);
      }
    }
    tick();
    const sec = window.setInterval(() => forceTick((x) => x + 1), 1000);
    return () => {
      stop = true;
      if (timer) window.clearTimeout(timer);
      window.clearInterval(sec);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  // 早退：加载中 / 查询失败
  if (!job) {
    return (
      <div className="job-panel">
        <div className="job-title">AI 图纸识别</div>
        <div className="job-title-sub">识别任务 #{jobId}</div>
        {err ? <div className="job-error-box"><div className="job-error-title">查询失败</div>
              <div className="job-error-msg">{err}</div></div>
             : <div className="job-loading">正在查询任务状态…</div>}
      </div>
    );
  }

  // ---------------------------------------------------------------- 派生状态
  const perf = (job.preprocess?.perf ?? {}) as Record<string, any>;
  const fa = (job.file_analysis ?? {}) as Record<string, any>;
  const pre = (job.preprocess ?? {}) as Record<string, any>;

  const cur = activeStepKey(job);
  const activeStep: Step = STEPS.find((s) => s.key === cur) ?? STEPS[0];
  const isFailed = job.status === 'FAILED' || job.status === 'TIMEOUT';
  const isCancelled = job.status === 'CANCELLED';
  const isDone = job.status === 'COMPLETED';

  const pct = Math.max(0, Math.min(100, Math.round(job.progress ?? 0)));
  const startedMs = parseUTCDate(job.started_at);
  const elapsedMs = startedMs ? Math.max(0, Date.now() - startedMs) : 0;
  const elapsedSec = Math.floor(elapsedMs / 1000);
  const ocrTotalMs = typeof perf.ocr_total_ms === 'number' ? perf.ocr_total_ms : null;
  const ocrInFlightMs = isDone ? null
    : (job.stage === 'OCR' ? elapsedMs : null);
  const nowMs = Date.now();
  const deadlineSec = job.deadline_at && !isDone && !isFailed
    ? Math.max(0, Math.floor((parseUTCDate(job.deadline_at) - nowMs) / 1000))
    : null;
  // 简单估算剩余：基于总超时预算 300s 减去当前已耗时（兜底用，不后端算）
  const estRemainSec = deadlineSec ?? Math.max(0, 300 - elapsedSec);

  // 文件信息行
  const fileExt = (job.ext ?? '').replace(/^\./, '').toUpperCase() || 'FILE';
  const pageNo = (job.page_index ?? 0) + 1;
  // 注意：这里不能使用任何 Hook（useMemo/useCallback），因为它在上方 `if (!job) return` 之后，
  // 若在此调用 Hook 会导致「job 从 null → 有值」时 Hook 数量变化，触发
  // "Rendered more hooks than during the previous render" 而整树白屏。
  const pageSizeLabel = (() => {
    const w = fa.width ?? pre.original_width ?? pre.image_width;
    const h = fa.height ?? pre.original_height ?? pre.image_height;
    if (typeof w === 'number' && typeof h === 'number' && w > 0 && h > 0) {
      const rs = (n: number) => Math.round(n).toLocaleString();
      return `${rs(w)} × ${rs(h)} pt`;
    }
    return null;
  })();

  // 阶段耗时映射（用于步骤右侧时间）
  const stageMs: Record<StepKey, number | null> = {
    read:    typeof perf.decode_ms === 'number' ? perf.decode_ms : null,
    convert: typeof perf.preprocess_ms === 'number' ? perf.preprocess_ms : null,
    ocr:     typeof perf.ocr_total_ms === 'number' ? perf.ocr_total_ms : null,
    parse:   typeof perf.parse_ms === 'number' ? perf.parse_ms : null,
    feature: null,
    balloon: typeof perf.job_total_ms === 'number' ? perf.job_total_ms : null,
  };

  // 各 step 的状态分类
  const stepStateOf = (key: StepKey): 'done' | 'active' | 'pending' | 'fail' | 'timeout' => {
    if (job.status === 'TIMEOUT' && key === cur) return 'timeout';
    if (isDone) return 'done';
    const order: StepKey[] = ['read', 'convert', 'ocr', 'parse', 'feature', 'balloon'];
    const ci = order.indexOf(cur);
    const ki = order.indexOf(key);
    if (ki < 0 || ci < 0) return 'pending';
    if (ki < ci) return 'done';
    if (ki === ci) return (isFailed ? 'fail' : 'active');
    return 'pending';
  };

  // 超时分级
  const ocrInLong = (ocrInFlightMs != null) && ocrInFlightMs > T_OCR_SLOW_MS;
  const jobInLong = !isDone && !isFailed && elapsedMs > T_JOB_SLOW_MS;

  // 当前阶段描述（OCR 运行中显示 hint，否则显示后端 message）
  const runningHint =
    !isDone && !isFailed
      ? (activeStep.hint || job.message)
      : '';

  // 完成态统计
  // job.feature_count 是已转换到表的 feature 行数；解析中无法精确拆分尺寸/GD&T，
  // 这里仅展示「Feature 合计 N」一项（如需拆分需后端在 perf 中暴露 feature_kind_count）。
  const featureCount = job.feature_count ?? 0;

  // ---------------------------------------------------------------- 操作
  const doRetry = async (lowRes: boolean) => {
    setBusy(true);
    try {
      const j = await retryJob(jobId, lowRes);
      setJob(j);
    } catch (e: any) { setErr(e?.message ?? '重试失败'); }
    finally { setBusy(false); }
  };
  const doCancel = async () => {
    setBusy(true);
    try { await cancelJob(jobId); onCancel?.(); }
    catch (e: any) { setErr(e?.message ?? '取消失败'); }
    finally { setBusy(false); }
  };
  const doBackground = () => { onCancel?.(); };

  // 后台运行：与 cancel（关闭弹窗，不杀后台）不同的语义——这里直接关闭浮层即可

  return (
    <div className="job-panel">
      {/* =================== 头部 =================== */}
      <div className="job-head">
        <div className="job-brand">
          <span className="job-brand-icon" aria-hidden>▣</span>
          <div className="job-brand-text">
            <div className="job-title">AI 图纸识别</div>
            <div className="job-title-sub">识别任务 #{job.id}</div>
          </div>
        </div>
        <span className={`job-status-badge st-${isDone ? 'completed' : isFailed ? (job.status === 'TIMEOUT' ? 'timeout' : 'failed') : isCancelled ? 'cancelled' : job.stage === 'OCR' ? 'ocr' : 'running'}`}>
          <i className="job-status-dot" />
          {isDone ? '已识别'
            : isFailed ? (job.status === 'TIMEOUT' ? 'OCR 识别超时' : '识别失败')
            : isCancelled ? '已取消'
            : job.stage === 'OCR' ? 'OCR 识别中'
            : (job.stage_cn || '处理中')}
        </span>
      </div>

      {/* =================== 文件信息 =================== */}
      <div className="job-fileblock">
        <div className="job-file-name" title={job.filename}>
          <span className="job-file-icon">📄</span>
          <span className="job-file-text">{job.filename}</span>
        </div>
        <div className="job-file-meta">
          {fileExt}
          {fa.page_count || job.page_index != null ? ` · Page ${pageNo}${fa.page_count && fa.page_count > 1 ? ` / ${fa.page_count}` : ''}` : ''}
          {pageSizeLabel ? ` · ${pageSizeLabel}` : ''}
          {job.low_res ? ' · 低分辨率' : ''}
        </div>
      </div>

      {/* =================== 当前阶段（高亮） =================== */}
      {(isDone || isFailed || isCancelled) ? null : (
        <div className="job-stage-head">
          <div className="job-stage-name">{activeStep.label}</div>
          <div className="job-stage-hint">{runningHint}</div>
        </div>
      )}

      {/* =================== 进度条 + 状态行 =================== */}
      <div className="job-progress-row">
        <div className={`job-bar ${isDone ? 'is-done' : ''} ${isFailed ? 'is-fail' : ''}`}>
          <div className="job-bar-fill" style={{ width: `${isFailed || isCancelled ? 0 : pct}%` }} />
        </div>
        <span className="job-pct">{isFailed || isCancelled ? 0 : pct}%</span>
      </div>

      <div className="job-status-row">
        <div className="job-status-cell">
          <div className="job-status-cell-key">当前阶段</div>
          <div className="job-status-cell-val">{isDone ? '已完成' : (activeStep.label)}</div>
        </div>
        <div className="job-status-cell">
          <div className="job-status-cell-key">已处理</div>
          <div className="job-status-cell-val">{isFailed || isCancelled ? '0%' : `${pct}%`}</div>
        </div>
        <div className="job-status-cell">
          <div className="job-status-cell-key">已耗时</div>
          <div className="job-status-cell-val">
            {isDone && ocrTotalMs != null ? fmtDuration(ocrTotalMs / 1000) : fmtDuration(elapsedSec)}
          </div>
        </div>
        <div className="job-status-cell">
          <div className="job-status-cell-key">预计剩余</div>
          <div className="job-status-cell-val">
            {isDone ? '—' : isFailed ? '—' : `约 ${Math.max(0, Math.round(estRemainSec / 5) * 5)}s`}
          </div>
        </div>
      </div>

      {/* =================== OCR 长时提示 =================== */}
      {ocrInLong && !isFailed && (
        <div className="job-warn">
          <span className="job-warn-icon">⚠</span>
          <span className="job-warn-text">
            OCR 处理时间较长（已 {fmtDuration(elapsedSec)}）。系统仍在继续，可以「后台运行」。
          </span>
        </div>
      )}
      {jobInLong && !ocrInLong && !isFailed && (
        <div className="job-warn subtle">
          <span className="job-warn-icon">ⓘ</span>
          <span className="job-warn-text">
            任务运行时间已超过 4 分钟，请耐心等待或在等待超过 5 分钟后系统自动超时。
          </span>
        </div>
      )}

      {/* =================== 步骤时间线 =================== */}
      <ol className="job-steps">
        {STEPS.map((s) => {
          const st = stepStateOf(s.key);
          const ms = stageMs[s.key];
          return (
            <li key={s.key} className={`job-step st-${st}`}>
              <span className="job-step-rail" />
              <span className="job-step-icon">
                {st === 'done' ? '✓' : st === 'active' ? '●' : st === 'fail' ? '×' : st === 'timeout' ? '⚠' : '○'}
              </span>
              <span className="job-step-label">{s.label}</span>
              <span className="job-step-time">
                {/* active 阶段实时显示「进行中 · 已 X」；done 阶段显示耗时（如有 perf） */}
                {st === 'active' && !isDone ? '进行中' : (ms != null ? fmtDuration(Math.round(ms / 1000)) : '')}
              </span>
            </li>
          );
        })}
      </ol>

      {/* =================== 状态消息 / 描述 =================== */}
      <div className="job-msgline">
        {isDone ? '✓ 图纸识别完成' : isFailed
          ? (job.status === 'TIMEOUT'
              ? '识别超过 300 秒自动终止。当前图纸可能分辨率过高或内容复杂。'
              : '识别未完成，请查看异常原因并选择重试。')
          : isCancelled ? '任务已取消。'
          : (job.message || activeStep.hint)}
      </div>

      {/* =================== 完成区 =================== */}
      {isDone && (
        <div className="job-done">
          <div className="job-done-line">
            <span className="job-done-key">总耗时</span>
            <span className="job-done-val">
              {fmtDuration((typeof perf.job_total_ms === 'number' ? perf.job_total_ms : elapsedMs) / 1000)}
            </span>
          </div>
          <div className="job-done-line">
            <span className="job-done-key">识别结果</span>
            <span className="job-done-val">
              Feature：<b>{featureCount}</b>
              {typeof perf.ocr_width === 'number' && typeof perf.ocr_height === 'number'
                ? <> · OCR 图：{perf.ocr_width}×{perf.ocr_height}</>
                : null}
              {typeof perf.cpu_rss_mb === 'number'
                ? <> · 内存 {perf.cpu_rss_mb}MB</>
                : null}
            </span>
          </div>
        </div>
      )}

      {/* =================== 异常区 =================== */}
      {isFailed && (
        <div className="job-error-box">
          <div className="job-error-title">
            {job.status === 'TIMEOUT' ? 'OCR 识别超时' : '识别失败'}
            {job.error_code ? `（${job.error_code}）` : ''}
          </div>
          {job.error?.message && <div className="job-error-msg">{job.error.message}</div>}

          {/* OCR 超时时给出常见原因清单 */}
          {job.status === 'TIMEOUT' && (
            <ul className="job-error-causes">
              <li>图像分辨率过高（建议原始 &gt; 8000px 时使用低分辨率模式）</li>
              <li>TIFF / PDF 页面过大（&gt; 50 MB）</li>
              <li>OCR 处理资源不足（CPU/内存被占用）</li>
              <li>图像内容过于复杂（密集小字）</li>
            </ul>
          )}

          {job.error?.detail && job.error_code === 'InternalError' && (
            <details className="job-error-detail">
              <summary>技术细节</summary>
              <div className="job-error-msg detail">{job.error.detail}</div>
            </details>
          )}
        </div>
      )}

      {/* =================== 查看详情（可折叠 debug） =================== */}
      <details className="job-debug" open={showDetail} onToggle={(e) => setShowDetail((e.target as HTMLDetailsElement).open)}>
        <summary>查看详情</summary>
        <div className="job-debug-grid">
          <div><span className="k">Job ID</span><span className="v">{job.id}</span></div>
          <div><span className="k">文件 ID</span><span className="v">{job.drawing_id ?? '—'}</span></div>
          <div><span className="k">状态 / 阶段</span><span className="v">{job.status} / {job.stage}</span></div>
          <div><span className="k">开始时间</span><span className="v">{job.started_at ?? '—'}</span></div>
          <div><span className="k">截止时间</span><span className="v">{job.deadline_at ?? '—'}</span></div>
          <div><span className="k">耗时</span><span className="v">{fmtDuration(elapsedSec)}</span></div>
          <div><span className="k">OCR 引擎</span><span className="v">{perf.engine ?? '—'}</span></div>
          <div><span className="k">图片尺寸</span><span className="v">
            {typeof pre.original_width === 'number' ? `${pre.original_width}×${pre.original_height}` : '—'}
          </span></div>
          <div><span className="k">OCR 图尺寸</span><span className="v">
            {typeof perf.ocr_width === 'number' ? `${perf.ocr_width}×${perf.ocr_height}` : '—'}
          </span></div>
          <div><span className="k">分块数</span><span className="v">{perf.tiles ?? 0}</span></div>
          <div><span className="k">CPU RSS</span><span className="v">{perf.cpu_rss_mb ? `${perf.cpu_rss_mb} MB` : '—'}</span></div>
        </div>
      </details>

      {/* =================== 操作区 =================== */}
      <div className="job-actions">
        {!TERMINAL.includes(job.status) ? (
          <>
            <button className="job-btn ghost" onClick={doBackground} disabled={busy}>后台运行</button>
            <button className="job-btn danger" onClick={doCancel} disabled={busy}>取消任务</button>
          </>
        ) : isDone ? (
          <button className="job-btn primary" onClick={onCancel} disabled={busy}>查看识别结果</button>
        ) : (
          <>
            <button className="job-btn primary" onClick={() => doRetry(false)} disabled={busy}>重新识别</button>
            <button className="job-btn" onClick={() => doRetry(true)} disabled={busy}>低分辨率模式</button>
            <button className="job-btn ghost" onClick={doCancel} disabled={busy}>关闭</button>
          </>
        )}
      </div>
    </div>
  );
}
